import type { ReactNode } from 'react';
import type { Entity } from '@backstage/catalog-model';
import { featureFlagsApiRef, type ApiHolder } from '@backstage/core-plugin-api';

// PAT-1027: `EntityPage.tsx` stays legacy JSX, and `convertLegacyAppRoot(routes,
// { entityPage })` turns each `EntityLayout.Route` into an entity content
// (`discovered-*`) of the alpha catalog page. Two things the legacy layout did
// at render time do not survive that conversion; these helpers cover them.

/** An `EntityLayout.Route` `if` predicate, as compat calls it. */
export type EntityRouteFilter = (
  entity: Entity,
  context?: { apis: ApiHolder },
) => boolean;

/**
 * Gates a tab on a feature flag. Compat only reads a route's `if`, so a
 * `<FeatureFlagged with>` wrapper around a route is ignored and the tab would
 * always show. Put the flag in the route's `if` instead:
 * `<EntityLayout.Route if={withFeatureFlag('orca')} ...>`, optionally combined
 * with the tab's own predicate.
 *
 * Fails closed: without an API holder (the legacy `EntityLayout` calls `if`
 * with the entity only) the flag cannot be read, so the tab is hidden.
 */
export const withFeatureFlag =
  (flag: string, predicate?: EntityRouteFilter): EntityRouteFilter =>
  (entity, context) =>
    context?.apis.get(featureFlagsApiRef)?.isActive(flag) === true &&
    (predicate?.(entity, context) ?? true);

/**
 * Plugin-less wrapper for a tab's content. Compat files a tab under the plugin
 * of its direct child: a tab whose child is an extension of a plugin also in
 * `features` (e.g. TechDocs' `EntityTechdocsContent`, catalog-graph's
 * `EntityCatalogGraphCard`) is replaced by that plugin and dropped, and one of
 * a legacy-only plugin (ADR, Census, ...) lands after all plugin-less tabs, out
 * of declaration order. Wrapping every tab's content keeps them all on the
 * converted orphan plugin, in the order they are declared. Plugins further
 * down the tree are still discovered, so their APIs stay registered.
 */
export const CompatTabContent = ({ children }: { children: ReactNode }) => (
  <>{children}</>
);
